import { useEffect } from 'react';
import { initializeNewRelicTrackingContainer } from '@vp/om-newrelic-tracker';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { H1, Spinner, FlexBox } from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import useIdentityContext from '../hooks/useIdentityContext';
import { useSiteData } from '../hooks/useSiteData';
import { NEWRELIC_BROWSER_AGENT_IDENTIFIER } from '../constants/newRelic';
import { QUERY_DEFAULT_OPTIONS } from '../constants/reactQueryDefaultConfig';
import { InternalViewProvider } from './contexts/InternalViewContext';
import { InternalViewOrderHistorySession } from './internalView/InternalViewOrderHistorySession';
import { OrderHistoryApplication } from './OrderHistoryApplication';

// Create a react-query client
const queryClient = new QueryClient();
queryClient.setDefaultOptions(QUERY_DEFAULT_OPTIONS);

export const OrderHistorySession = () => {
  const [orderHistoryPageMetadata, sharedActionMessages] = useTranslations([
    'orderHistoryPageMetadata',
    'sharedActionMessages',
  ]);
  const { isIdentityInitialized, identity, auth } = useIdentityContext();
  const { isSignedIn, isAnonymousUser, shopperId, isInternalUser } = identity;
  const { siteName, locale, vistacartTenant: tenant } = useSiteData();

  if (isIdentityInitialized && isAnonymousUser && auth) {
    auth.signIn();
  }

  useEffect(() => {
    if (shopperId && shopperId !== '') {
      initializeNewRelicTrackingContainer({
        agentId: NEWRELIC_BROWSER_AGENT_IDENTIFIER,
        staticApplicationData: {
          shopperId,
          locale,
          site: siteName,
          tenant,
        },
      });
    }
  }, [locale, shopperId, siteName, tenant]);

  if (isInternalUser) {
    return (
      <QueryClientProvider client={queryClient}>
        <InternalViewProvider>
          <InternalViewOrderHistorySession />
        </InternalViewProvider>
      </QueryClientProvider>
    );
  }

  return isSignedIn ? (
    <QueryClientProvider client={queryClient}>
      <OrderHistoryApplication />
    </QueryClientProvider>
  ) : (
    <>
      <H1 visuallyHidden>{orderHistoryPageMetadata?.['pageTitle']}</H1>
      <FlexBox justifyContent="center">
        <Spinner
          accessibleText={sharedActionMessages?.['loading']}
          aria-busy="true"
          aria-live="polite"
          role="status"
          size="super"
        />
      </FlexBox>
    </>
  );
};
